import { useState } from "react";
import PageHeader from "@/components/PageHeader";
import DataTable from "@/components/DataTable";
import OperationDialog from "@/components/OperationDialog";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { PackagePlus, Plus, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useOptions } from "@/lib/useOptions";
import api from "@/lib/api";
import { toast } from "sonner";

export default function Inward() {
  const { can } = useAuth();
  const [open, setOpen] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const warehouses = useOptions("/warehouses");
  const vendors = useOptions("/vendors");

  const approve = async (row, reload) => {
    try { await api.post(`/grn/${row.id}/approve`); toast.success(`${row.grn_no} approved — stock received`); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const cols = [
    { key: "grn_no", label: "GRN No", mono: true, render: (r) => <span className="font-mono font-medium">{r.grn_no}</span> },
    { key: "vendor_name", label: "Vendor" },
    { key: "invoice_no", label: "Invoice", mono: true },
    { key: "items", label: "Lines", render: (r) => (r.items || []).length },
    { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
    { key: "created_at", label: "Date", render: (r) => new Date(r.created_at).toLocaleDateString() },
  ];

  const fields = [
    { name: "vendor_name", label: "Vendor", type: "select", required: true, options: vendors.map((v) => ({ value: v.label, label: v.label })) },
    { name: "invoice_no", label: "Invoice No" },
    { name: "warehouse_id", label: "Warehouse", type: "select", required: true, options: warehouses },
  ];

  return (
    <div>
      <PageHeader title="Inward / GRN" subtitle="Goods receipt, inspection & approval" icon={PackagePlus}
        breadcrumb={["Home", "Operations", "Inward"]}
        actions={can("inward.create") && (
          <Button data-testid="add-grn-btn" onClick={() => setOpen(true)} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="h-4 w-4 mr-1.5" />New GRN
          </Button>
        )} />
      <DataTable endpoint="/grn" columns={cols} testId="grn-table" refreshKey={refresh}
        actions={(row, reload) => row.status === "pending" && can("inward.approve") && (
          <Button size="sm" variant="outline" data-testid={`approve-grn-${row.id}`} onClick={() => approve(row, reload)}
            className="text-emerald-600 border-emerald-200 hover:bg-emerald-50">
            <CheckCircle2 className="h-4 w-4 mr-1" />Approve
          </Button>
        )} />
      <OperationDialog open={open} onOpenChange={setOpen} title="New Goods Receipt Note" headerFields={fields}
        endpoint="/grn" onSaved={() => setRefresh((r) => r + 1)} />
    </div>
  );
}
